import React, { useEffect, useRef, useState } from "react";
import { Camera, X, RotateCcw, Check } from "lucide-react";

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

export default function PhotoCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [snapshot, setSnapshot] = useState(null);
  const [useFileFallback, setUseFileFallback] = useState(isIOS()); // iOS → native camera

  const startCamera = async () => {
    if (useFileFallback) return;
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        try { await videoRef.current.play(); } catch (e) { /* ignored */ }
      }
    } catch (e) {
      // No setState here — user gets a "Use native camera" button rendered alongside the preview.
      console.warn("Camera access denied or unavailable:", e?.message);
    }
  };

  // Start (or restart) the camera whenever fallback mode flips.
  useEffect(() => {
    if (!useFileFallback) {
      startCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useFileFallback]);

  // Stop any active media tracks on unmount.
  useEffect(() => () => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
  }, []);

  // Auto-open native camera on iOS once mounted
  useEffect(() => {
    if (useFileFallback && fileInputRef.current && !snapshot) {
      // Slight delay so the modal renders first
      const t = setTimeout(() => fileInputRef.current?.click(), 100);
      return () => clearTimeout(t);
    }
  }, [useFileFallback, snapshot]);

  const snap = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 640;
    const h = v.videoHeight || 480;
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => setSnapshot(blob), "image/jpeg", 0.85);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      // User cancelled the iOS camera — close the modal cleanly
      onClose?.();
      return;
    }
    setSnapshot(f);
  };

  const retake = () => {
    setSnapshot(null);
    if (useFileFallback) {
      // Re-open native camera
      setTimeout(() => fileInputRef.current?.click(), 50);
    }
  };

  const confirm = () => { if (snapshot) onCapture(snapshot); };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" data-testid="photo-overlay">
      <div className="brut-card w-full max-w-md bg-white">
        <div className="flex items-center justify-between border-b-2 border-black px-4 py-3">
          <div className="flex items-center gap-2 font-heading font-black text-lg">
            <Camera strokeWidth={3} size={18} /> LIVE PHOTO
          </div>
          <button onClick={onClose} className="brut-btn brut-btn-secondary !py-1 !px-2" data-testid="photo-close-btn">
            <X size={16} strokeWidth={3} />
          </button>
        </div>
        <div className="p-4">
          {/* Always render file input (used on iOS or as fallback) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFile}
            className="hidden"
            data-testid="photo-file-input"
          />

          {!snapshot ? (
            <>
              {useFileFallback ? (
                <div className="border-2 border-black p-6 text-center bg-[#F4F4F0]">
                  <Camera size={40} strokeWidth={2.5} className="mx-auto mb-3" />
                  <div className="font-bold text-sm uppercase tracking-wider">Camera will open on your device</div>
                  <div className="text-xs text-[#666] mt-2">If it didn&apos;t open, tap below.</div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="brut-btn brut-btn-primary mt-4"
                    data-testid="ios-open-camera-btn"
                  >
                    <Camera size={14} strokeWidth={3} /> OPEN CAMERA
                  </button>
                </div>
              ) : (
                <>
                  <div className="border-2 border-black overflow-hidden bg-black aspect-[4/3]">
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      playsInline
                      muted
                      autoPlay
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button onClick={snap} className="brut-btn brut-btn-primary" data-testid="photo-snap-btn">
                      <Camera size={14} strokeWidth={3} /> CAPTURE
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="brut-btn brut-btn-secondary"
                      data-testid="photo-fallback-btn"
                    >
                      USE NATIVE
                    </button>
                  </div>
                  <div className="text-xs text-[#666] uppercase tracking-wider mt-2">
                    ▸ If preview is black, tap USE NATIVE.
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="border-2 border-black overflow-hidden aspect-[4/3]">
                <img src={URL.createObjectURL(snapshot)} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex gap-3 mt-4">
                <button onClick={retake} className="brut-btn brut-btn-secondary flex-1" data-testid="photo-retake-btn">
                  <RotateCcw size={16} strokeWidth={3} /> RETAKE
                </button>
                <button onClick={confirm} className="brut-btn brut-btn-primary flex-1" data-testid="photo-confirm-btn">
                  <Check size={16} strokeWidth={3} /> USE
                </button>
              </div>
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      </div>
    </div>
  );
}
